import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Loader2, 
  FileText,
  Shield,
  CreditCard,
  Check,
  Clock,
  Download,
  AlertCircle,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import { legalServicesAPI } from "../lib/api";

const STATUS_CONFIG = {
  pending_payment: { label: "En attente de paiement", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  paid: { label: "Payé - En traitement", color: "bg-blue-100 text-blue-700", icon: Clock },
  in_progress: { label: "En cours", color: "bg-purple-100 text-purple-700", icon: Clock },
  completed: { label: "Terminé", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  cancelled: { label: "Annulé", color: "bg-red-100 text-red-700", icon: AlertCircle }
};

const SERVICE_CONFIG = {
  regulation: { label: "Rédaction de règlement", icon: FileText, color: "from-blue-500 to-indigo-600" },
  bailiff: { label: "Contrôle huissier", icon: Shield, color: "from-amber-500 to-orange-600" },
  bundle: { label: "Règlement + Huissier", icon: Shield, color: "from-purple-500 to-pink-600" }
};

export default function LegalServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadOrder();
    
    // Check for payment callback
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (payment === "success" && sessionId) {
      checkPaymentStatus();
    } else if (payment === "cancelled") {
      toast.error("Paiement annulé");
    }
  }, [id]);

  const loadOrder = async () => {
    try {
      const res = await legalServicesAPI.get(id);
      setOrder(res.data);
    } catch (error) {
      toast.error("Commande non trouvée");
      navigate("/services");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const res = await legalServicesAPI.checkPayment(id);
      if (res.data.paid) {
        toast.success("Paiement confirmé ! Votre commande est en cours de traitement.");
        loadOrder();
      }
    } catch (error) {
      console.error("Error checking payment:", error);
    }
  };

  const handlePay = async () => {
    setProcessing(true);
    try {
      const res = await legalServicesAPI.checkout(id);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du paiement");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending_payment;
  const serviceConfig = SERVICE_CONFIG[order.service_type] || SERVICE_CONFIG.regulation;
  const StatusIcon = statusConfig.icon;
  const ServiceIcon = serviceConfig.icon;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/services">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>
              Commande #{order.id.slice(0, 8)}
            </h1>
            <p className="text-sm text-muted-foreground">
              Service juridique
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid gap-6">
          {/* Status Card */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${serviceConfig.color} flex items-center justify-center`}>
                    <ServiceIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{serviceConfig.label}</h2>
                    <p className="text-muted-foreground">Pour : {order.draw_title}</p>
                  </div>
                </div>
                <Badge className={statusConfig.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Détails de la commande</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type de service</span>
                  <span className="font-medium">{serviceConfig.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tirage concerné</span>
                  <span className="font-medium">{order.draw_title}</span>
                </div>
                {order.draw_date && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date du tirage</span>
                    <span className="font-medium">
                      {new Date(order.draw_date).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Créée le</span>
                  <span className="font-medium">
                    {new Date(order.created_at).toLocaleDateString("fr-FR")}
                  </span>
                </div>
                {order.notes && (
                  <div className="pt-2 border-t">
                    <p className="text-sm text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{order.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Paiement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Prix HT</span>
                  <span className="font-medium">{order.price_ht.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">TVA (20%)</span>
                  <span className="font-medium">{(order.price_ttc - order.price_ht).toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-4">
                  <span>Total TTC</span>
                  <span>{order.price_ttc.toFixed(2)} €</span>
                </div>
                
                {order.status === "pending_payment" && (
                  <Button 
                    onClick={handlePay} 
                    disabled={processing}
                    className="w-full mt-4 rounded-full"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Payer maintenant
                  </Button>
                )}
                
                {order.paid_at && (
                  <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">
                      Payé le {new Date(order.paid_at).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bailiff Info (if completed) */}
          {(order.bailiff_name || order.regulation_url) && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Documents et informations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {order.bailiff_name && (
                  <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
                    <Shield className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium">Huissier désigné</p>
                      <p className="text-muted-foreground">{order.bailiff_name}</p>
                      {order.bailiff_info && (
                        <p className="text-sm text-muted-foreground mt-1">{order.bailiff_info}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {order.regulation_url && (
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium">Règlement officiel</p>
                        <p className="text-sm text-muted-foreground">Document PDF téléchargeable</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={order.regulation_url} target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                      </a>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Help Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Besoin d'aide ?</p>
                <p className="text-sm text-blue-700">
                  Notre équipe juridique vous contactera sous 24h après le paiement. 
                  Pour toute question, contactez-nous à juridique@tireo.app
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
